import {
  Box,
  Button,
  Col,
  Feedback,
  Icon,
  Row,
  Text,
} from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import translations from 'src/lang';
import { useDispatch } from 'react-redux';
import { useAudienceActivationContext } from 'src/modules/audience-activation/providers/AudienceActivationProvider';
import { showEditAudienceModal } from 'src/actions/CreateAudienceModalActions';
import { sendEvent } from 'src/actions/EventsActions';
import { TARGET_MESSAGE_CODE } from '../../api/request';

const lang = 'en-US';
const translation = translations[lang].MODALS.AUDIENCE_ACTIVATION;

const StyledReloadButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 13rem;
  width: 100%;
`;

const StyledEditAudienceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 13rem;
  width: 310px;
  margin: 4rem auto;
`;

interface ActivationErrorContainerProps {
  errorCode: keyof typeof TARGET_MESSAGE_CODE;
}

export const ActivationErrorContainer: FC<ActivationErrorContainerProps> = ({
  errorCode,
}) => {
  const dispatch = useDispatch();
  const { repositoryItem, closeModal } = useAudienceActivationContext();

  const isAudienceRemoved = errorCode === TARGET_MESSAGE_CODE.NO_AUDIENCE_FOUND;
  const isWide = errorCode === TARGET_MESSAGE_CODE.AUDIENCE_WIDE;
  const isNarrow = errorCode === TARGET_MESSAGE_CODE.AUDIENCE_NARROW;
  const isAudienceWideOrNarrow = isWide || isNarrow;

  const handleEditAudience = useCallback(() => {
    dispatch(showEditAudienceModal({ audience: repositoryItem }));
    dispatch(sendEvent('Workspace'));
    closeModal();
  }, [repositoryItem, dispatch, closeModal]);

  const handleReload = useCallback(() => window.location.reload(), []);

  if (isAudienceWideOrNarrow) {
    return (
      <div>
        <Col span="auto">
          <Box pB="large">
            <Box pB="small">
              <Row valign="middle" gutter="small">
                <Col span="auto">
                  <Icon size="medium" icon="icon-social" />
                </Col>
                <Col span="auto">
                  <Text lettercase="capitalize" variant="dark" size="small">
                    {translation.CONTENT_TITLE}
                  </Text>
                </Col>
              </Row>
            </Box>

            <Text variant="dark_lighter" size="small" className="paragraph">
              {translation.CONTENT_DESCRIPTION}
            </Text>
          </Box>
          <Feedback
            message={translation.ERROR_MESSAGES[errorCode]}
            status="error"
          />
        </Col>
        <StyledEditAudienceContainer>
          <Icon icon="edit" size="xlarge" variant="secondary" />
          <Box pT="medium" pB="medium">
            <Text variant="dark" size="xbody" align="center">
              {isWide
                ? translation.EDIT_AUDIENCE.TEXT_PARAGRAPH_WIDE_1
                : translation.EDIT_AUDIENCE.TEXT_PARAGRAPH_NARROW_1}
            </Text>
            <Text variant="dark" size="xbody" align="center">
              {isWide
                ? translation.EDIT_AUDIENCE.TEXT_PARAGRAPH_WIDE_2
                : translation.EDIT_AUDIENCE.TEXT_PARAGRAPH_NARROW_2}
            </Text>
          </Box>
          <Button size="small" variant="standard" onClick={handleEditAudience}>
            {translation.BUTTON_EDIT_AUDIENCE}
          </Button>
        </StyledEditAudienceContainer>
      </div>
    );
  }

  return (
    <Box mB="small">
      <Feedback
        message={
          isAudienceRemoved
            ? translation.ERROR_MESSAGES.AUDIENCE_REMOVED
            : translation.ERROR_MESSAGES.GENERAL_ERROR
        }
        status="error"
      />
      {!isAudienceRemoved && (
        <StyledReloadButtonContainer>
          <Button size="small" variant="standard" onClick={handleReload}>
            {translation.BUTTON_RELOAD}
          </Button>
        </StyledReloadButtonContainer>
      )}
    </Box>
  );
};
